import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Users,
  Calendar,
  DollarSign,
  TrendingUp,
  UserX,
  UserCheck,
  Star,
  StarOff,
  Download,
} from 'lucide-react';

const AdminDashboard = () => {
  const { api } = useAuth();
  const { t, formatCurrency, formatDate } = useLanguage();

  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [weddings, setWeddings] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, usersRes, weddingsRes, transactionsRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/users'),
        api.get('/admin/weddings'),
        api.get('/admin/transactions'),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setWeddings(weddingsRes.data);
      setTransactions(transactionsRes.data);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  }, [api]);

  const handleBanUser = async (userId, banned) => {
    try {
      await api.put(`/admin/users/${userId}/ban?banned=${banned}`);
      toast.success(banned ? 'User banned' : 'User unbanned');
      fetchData();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleFeatureWedding = async (weddingId, featured) => {
    try {
      await api.put(`/admin/weddings/${weddingId}/feature?featured=${featured}`);
      toast.success(featured ? 'Wedding featured' : 'Wedding unfeatured');
      fetchData();
    } catch (error) {
      toast.error('Failed to update wedding');
    }
  };

  const handleVerifyWedding = async (weddingId, action, reason) => {
    try {
      await api.post(`/admin/weddings/${weddingId}/verify`, { action, reason });
      toast.success(`Wedding ${action}ed`);
      fetchData();
    } catch (error) {
      toast.error('Failed to verify wedding');
    }
  };

  const handleCompleteWedding = async (weddingId) => {
    try {
      await api.put(`/admin/weddings/${weddingId}/complete`);
      toast.success('Wedding marked as completed');
      fetchData();
    } catch (error) {
      toast.error('Failed to complete wedding');
    }
  };

  const exportToCSV = (data, filename) => {
    if (!data || data.length === 0) return;
    
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => headers.map(h => JSON.stringify(row[h] || '')).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-secondary py-8">
          <div className="max-w-7xl mx-auto px-4">
            <Skeleton className="h-10 w-48 mb-8" />
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              {[...Array(8)].map((_, i) => (
                <Skeleton key={i} className="h-32" />
              ))}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-secondary" data-testid="admin-dashboard">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="font-serif text-3xl font-bold">{t('admin.title')}</h1>
            <p className="text-muted-foreground">Platform overview and management</p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">{t('admin.stats.users')}</p>
                    <p className="text-2xl font-bold">{stats?.total_users || 0}</p>
                  </div>
                  <Users className="h-6 w-6 text-primary" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">{t('admin.stats.hosts')}</p>
                    <p className="text-2xl font-bold">{stats?.total_hosts || 0}</p>
                  </div>
                  <Users className="h-6 w-6 text-primary" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">{t('admin.stats.guests')}</p>
                    <p className="text-2xl font-bold">{stats?.total_guests || 0}</p>
                  </div>
                  <Users className="h-6 w-6 text-primary" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">{t('admin.stats.weddings')}</p>
                    <p className="text-2xl font-bold">{stats?.total_weddings || 0}</p>
                  </div>
                  <Calendar className="h-6 w-6 text-primary" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">{t('admin.stats.activeWeddings')}</p>
                    <p className="text-2xl font-bold">{stats?.active_weddings || 0}</p>
                  </div>
                  <Calendar className="h-6 w-6 text-green-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">{t('admin.stats.applications')}</p>
                    <p className="text-2xl font-bold">{stats?.total_applications || 0}</p>
                  </div>
                  <TrendingUp className="h-6 w-6 text-primary" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">{t('admin.stats.revenue')}</p>
                    <p className="text-2xl font-bold">{formatCurrency(stats?.total_revenue || 0)}</p>
                  </div>
                  <DollarSign className="h-6 w-6 text-primary" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">{t('admin.stats.platformEarnings')}</p>
                    <p className="text-2xl font-bold text-primary">{formatCurrency(stats?.platform_earnings || 0)}</p>
                  </div>
                  <DollarSign className="h-6 w-6 text-primary" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Data Tabs */}
          <Tabs defaultValue="users">
            <div className="flex justify-between items-center mb-4">
              <TabsList>
                <TabsTrigger value="users" data-testid="admin-tab-users">
                  {t('admin.tabs.users')}
                </TabsTrigger>
                <TabsTrigger value="weddings" data-testid="admin-tab-weddings">
                  {t('admin.tabs.weddings')}
                </TabsTrigger>
                <TabsTrigger value="transactions" data-testid="admin-tab-transactions">
                  {t('admin.tabs.transactions')}
                </TabsTrigger>
                <TabsTrigger value="verification" data-testid="admin-tab-verification">
                  {t('admin.tabs.verification')}
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="users">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Users</CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => exportToCSV(users, 'users')}
                    data-testid="export-users"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Export CSV
                  </Button>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Rating</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map((u) => (
                        <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                          <TableCell className="font-medium">{u.name}</TableCell>
                          <TableCell>{u.email}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize">
                              {u.role}
                            </Badge>
                          </TableCell>
                          <TableCell>{u.rating?.toFixed(1) || '-'}</TableCell>
                          <TableCell>
                            {u.banned ? (
                              <Badge variant="destructive">Banned</Badge>
                            ) : (
                              <Badge variant="outline" className="text-green-600">Active</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {u.role !== 'admin' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleBanUser(u.id, !u.banned)}
                                data-testid={`ban-user-${u.id}`}
                              >
                                {u.banned ? (
                                  <UserCheck className="h-4 w-4 text-green-600" />
                                ) : (
                                  <UserX className="h-4 w-4 text-red-600" />
                                )}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="weddings">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Weddings</CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => exportToCSV(weddings, 'weddings')}
                    data-testid="export-weddings"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Export CSV
                  </Button>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Title</TableHead>
                        <TableHead>Host</TableHead>
                        <TableHead>Location</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Price</TableHead>
                        <TableHead>Slots</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {weddings.map((w) => (
                        <TableRow key={w.id} data-testid={`wedding-row-${w.id}`}>
                          <TableCell className="font-medium">
                            {w.is_featured && <Star className="inline h-4 w-4 text-yellow-500 mr-1" />}
                            {w.title}
                          </TableCell>
                          <TableCell>{w.host_name}</TableCell>
                          <TableCell>{w.location}, {w.country}</TableCell>
                          <TableCell>{formatDate(w.date)}</TableCell>
                          <TableCell>{formatCurrency(w.price_per_guest, w.currency)}</TableCell>
                          <TableCell>{w.available_slots}/{w.total_slots}</TableCell>
                          <TableCell>
                            <Badge variant={w.status === 'open' ? 'default' : 'secondary'}>
                              {w.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleFeatureWedding(w.id, !w.is_featured)}
                                data-testid={`feature-wedding-${w.id}`}
                              >
                                {w.is_featured ? (
                                  <StarOff className="h-4 w-4 text-yellow-500" />
                                ) : (
                                  <Star className="h-4 w-4" />
                                )}
                              </Button>
                              {w.status === 'open' && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    if (window.confirm(`Mark "${w.title}" as completed? This enables reviews.`)) {
                                      handleCompleteWedding(w.id);
                                    }
                                  }}
                                  data-testid={`complete-wedding-${w.id}`}
                                >
                                  Complete
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="verification">
              <Card>
                <CardHeader>
                  <CardTitle>Pending Verification ({weddings.filter((w) => w.verification_status === 'pending').length})</CardTitle>
                </CardHeader>
                <CardContent>
                  {weddings.filter((w) => w.verification_status === 'pending').length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No weddings pending verification</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Title</TableHead>
                          <TableHead>Host</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {weddings
                          .filter((w) => w.verification_status === 'pending')
                          .map((w) => (
                            <TableRow key={w.id} data-testid={`verify-row-${w.id}`}>
                              <TableCell className="font-medium">{w.title}</TableCell>
                              <TableCell>{w.host_name}</TableCell>
                              <TableCell>{formatDate(w.date)}</TableCell>
                              <TableCell>
                                <Badge variant="secondary">Pending</Badge>
                              </TableCell>
                              <TableCell>
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="default"
                                    onClick={() => handleVerifyWedding(w.id, 'approve')}
                                    data-testid={`approve-wedding-${w.id}`}
                                  >
                                    Approve
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      const reason = window.prompt('Reason for rejection:');
                                      if (reason) handleVerifyWedding(w.id, 'reject', reason);
                                    }}
                                    data-testid={`reject-wedding-${w.id}`}
                                  >
                                    Reject
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            <TabsContent value="transactions">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Transactions</CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => exportToCSV(transactions, 'transactions')}
                    data-testid="export-transactions"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Export CSV
                  </Button>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>ID</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Platform Fee</TableHead>
                        <TableHead>Payout</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {transactions.map((tx) => (
                        <TableRow key={tx.id} data-testid={`tx-row-${tx.id}`}>
                          <TableCell className="font-mono text-sm">{tx.id.slice(0, 8)}...</TableCell>
                          <TableCell>{formatCurrency(tx.total_amount, tx.currency)}</TableCell>
                          <TableCell className="text-primary">{formatCurrency(tx.platform_fee, tx.currency)}</TableCell>
                          <TableCell>{formatCurrency(tx.payout_amount, tx.currency)}</TableCell>
                          <TableCell>
                            {tx.status === 'refunded' ? (
                              <Badge variant="outline" className="text-orange-600 border-orange-600">
                                {tx.status}
                              </Badge>
                            ) : (
                              <Badge variant={tx.status === 'paid' ? 'default' : 'secondary'}>
                                {tx.status}
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>{formatDate(tx.created_at)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </Layout>
  );
};

export default AdminDashboard;
